import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, FileArchive, AlertCircle, Loader2, ShieldCheck, Zap, Eye } from 'lucide-react'
import { uploadJar, startScan } from '../lib/api'
import styles from './HomePage.module.css'

const MAX_SIZE_MB = 25

export default function HomePage() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const [dragOver, setDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'starting' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const validateFile = (file: File): string | null => {
    if (!file.name.toLowerCase().endsWith('.jar')) {
      return 'Obsługiwane są tylko pliki .jar'
    }
    if (file.size === 0) return 'Plik jest pusty'
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `Plik jest zbyt duży. Maksymalny rozmiar to ${MAX_SIZE_MB} MB`
    }
    return null
  }

  const handleFile = useCallback((file: File) => {
    const error = validateFile(file)
    if (error) { setErrorMessage(error); return }
    setSelectedFile(file)
    setErrorMessage(null)
  }, [])

  const onDragOver  = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragOver(true) }, [])
  const onDragLeave = useCallback(() => setDragOver(false), [])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }, [handleFile])

  const onScanClick = async () => {
    if (!selectedFile) return
    setErrorMessage(null)
    setUploadProgress(0)
    setPhase('uploading')
    try {
      const uploadResult = await uploadJar(selectedFile, setUploadProgress)
      setPhase('starting')
      await startScan(uploadResult.scanId, uploadResult.fileKey)
      navigate(`/scan/${uploadResult.scanId}`)
    } catch (err) {
      setPhase('error')
      setErrorMessage(err instanceof Error ? err.message : 'Wystąpił nieznany błąd')
    }
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const isLoading = phase === 'uploading' || phase === 'starting'

  return (
    <div className={styles.root}>
      {/* ── Strefa uploadu ───────────────────────────────────────────────── */}
      <div className={styles.uploadSection}>
        <div
          className={`${styles.dropzone} ${dragOver ? styles.dropzoneDragging : ''} ${selectedFile ? styles.dropzoneHasFile : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !isLoading && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Wgraj plik JAR"
          onKeyDown={(e) => e.key === 'Enter' && !isLoading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".jar"
            onChange={onInputChange}
            className={styles.hiddenInput}
            aria-label="Wybierz plik JAR"
            disabled={isLoading}
          />

          {selectedFile ? (
            <div className={styles.filePreview}>
              <div className={styles.fileIcon}><FileArchive size={32} /></div>
              <div className={styles.fileInfo}>
                <span className={styles.fileName}>{selectedFile.name}</span>
                <span className={styles.fileSize}>{formatSize(selectedFile.size)}</span>
              </div>
              {!isLoading && (
                <button
                  className={styles.changeFileBtn}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedFile(null)
                    setPhase('idle')
                    if (inputRef.current) inputRef.current.value = ''
                  }}
                  aria-label="Usuń wybrany plik"
                >
                  Zmień plik
                </button>
              )}
            </div>
          ) : (
            <div className={styles.dropzoneContent}>
              <div className={styles.dropzoneIcon}><Upload size={36} /></div>
              <p className={styles.dropzoneTitle}>Upuść plik JAR tutaj</p>
              <p className={styles.dropzoneSubtitle}>
                lub kliknij, aby wybrać — maks. {MAX_SIZE_MB} MB
              </p>
            </div>
          )}
        </div>

        {errorMessage && (
          <div className={styles.errorBanner} role="alert">
            <AlertCircle size={16} />
            <span>{errorMessage}</span>
          </div>
        )}

        {phase === 'uploading' && (
          <div className={styles.progressBlock}>
            <div className={styles.progressLabel}>
              <span>Przesyłanie...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {phase === 'starting' && (
          <div className={styles.startingBanner}>
            <Loader2 size={16} className="animate-spin" />
            <span>Uruchamianie analizy...</span>
          </div>
        )}

        <button
          className={styles.scanBtn}
          onClick={onScanClick}
          disabled={!selectedFile || isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {phase === 'uploading' ? 'Przesyłanie...' : 'Uruchamianie skanowania...'}
            </>
          ) : (
            <>
              <ShieldCheck size={18} />
              Skanuj w poszukiwaniu malware
            </>
          )}
        </button>
      </div>

      {/* ── Karty funkcji ────────────────────────────────────────────────── */}
      <div className={styles.features}>
        <div className={styles.featureCard}>
          <div className={styles.featureIcon} style={{ color: '#22d3ee' }}>
            <Zap size={22} />
          </div>
          <h3 className={styles.featureTitle}>2 modele równolegle</h3>
          <p className={styles.featureDesc}>
            Kimi K3 (60%) i Llama 3.2 90B (40%) analizują jednocześnie.
            Wyniki są agregowane w zbiorczy wskaźnik zagrożenia.
          </p>
        </div>

        <div className={styles.featureCard}>
          <div className={styles.featureIcon} style={{ color: '#a855f7' }}>
            <Eye size={22} />
          </div>
          <h3 className={styles.featureTitle}>Głęboka analiza kodu</h3>
          <p className={styles.featureDesc}>
            Pliki JAR są dekompilowane przez CFR do czytelnego kodu Java. Modele AI
            sprawdzają każdą klasę pod kątem backdoorów, minerów, obfuskacji i eksfiltracji.
          </p>
        </div>

        <div className={styles.featureCard}>
          <div className={styles.featureIcon} style={{ color: '#22c55e' }}>
            <ShieldCheck size={22} />
          </div>
          <h3 className={styles.featureTitle}>Szczegółowe raporty</h3>
          <p className={styles.featureDesc}>
            Wskaźnik zagrożenia (0–100), skategoryzowane znaleziska oraz podejrzany kod
            podświetlony bezpośrednio w widoku źródłowym.
          </p>
        </div>
      </div>

      {/* ── Legenda kategorii zagrożeń ───────────────────────────────────── */}
      <div className={styles.categories}>
        <h2 className={styles.categoriesTitle}>Wykrywane kategorie zagrożeń</h2>
        <div className={styles.categoriesList}>
          {[
            { label: 'Backdoor',              color: '#ef4444' },
            { label: 'Kryptominer',           color: '#f59e0b' },
            { label: 'Obfuskacja',            color: '#8b5cf6' },
            { label: 'Eksfiltracja danych',   color: '#ec4899' },
            { label: 'RAT',                   color: '#dc2626' },
            { label: 'Spyware',               color: '#f97316' },
            { label: 'Ransomware',            color: '#ef4444' },
            { label: 'Dropper',               color: '#e879f9' },
            { label: 'Podejrzana sieć',       color: '#3b82f6' },
            { label: 'Podejrzana refleksja',  color: '#06b6d4' },
            { label: 'Niebezpieczny ClassLoader', color: '#10b981' },
          ].map(({ label, color }) => (
            <span key={label} className={styles.categoryTag} style={{ borderColor: color, color }}>
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
